import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import ResponsiveAdminLayout from '../components/ResponsiveAdminLayout';
import AdminLogin from '../pages/AdminLogin';
import ResponsiveDashboard from '../components/ResponsiveDashboard';
import AdminTicketingListPage from '../ticketing/pages/AdminTicketingListPage';
import AdminTicketingTicketPage from '../ticketing/pages/AdminTicketingTicketPage';
import ResponsiveSettings from '../components/ResponsiveSettings';
import { PageSkeleton } from '../../components/loading/skeletons';
import CreateAdmin from '../components/admin-management/CreateAdmin';
import InviteCreateAdmin from '../pages/admin-management/InviteCreateAdmin';
import UserList from '../components/user-management/UserList/UserList';
import UserProfile from '../components/user-management/UserProfile/UserProfile';
import { ImpersonationProvider } from '../impersonation/ImpersonationContext';
import ImpersonationPage from '../pages/ImpersonationPage';
import AdminSetup from '../pages/setup/AdminSetup';
import PackageManagement from '../pages/PackageManagement';
import PackageGracePeriodTestPage from '../pages/packages/PackageGracePeriodTestPage';
import PackageFormPage from '../pages/packages/PackageFormPage';
import Demos from '../pages/Demos';
import AllSignups from '../pages/AllSignups';
import { AdminThemeProvider } from '../context/AdminThemeContext';
import { MaintenanceProvider } from '../context/MaintenanceContext';
import { WebsiteMaintenanceProvider } from '../../website/context/WebsiteMaintenanceContext';
import AuthorizeManualCharge from '../pages/AuthorizeManualCharge';
import AdminTools from '../pages/AdminTools';
import AppSettings from '../pages/app-settings/AppSettings';
import Transactions from '../pages/Transactions';
import SMSLogs from '../pages/sms-logs/SMSLogs';
import ImpersonationLogs from '../pages/impersonation-logs/ImpersonationLogs';
import AdminPermissionRoute from '../components/ProtectedAdminRoute';
import { ADMIN_PERMISSIONS } from '../constants/permissions';
import { useAdminPermissions } from '../hooks/useAdminPermissions';
import AdminCompanyProfile from '../company-profile/pages/AdminCompanyProfile';
import AdminPurgeCompanyPage from '../company-profile/pages/AdminPurgeCompanyPage';
import SuperAdminRoute from '../components/SuperAdminRoute';
import AdminInviteAcceptPage from '../invitations/pages/AdminInviteAcceptPage';
import AdminInvitesPage from '../invitations/pages/AdminInvitesPage';
import AdminMagicLinkPage from '../magic-link-auth/pages/AdminMagicLinkPage';
import AdminForgotPasswordPage from '../admin-auth/pages/AdminForgotPasswordPage';
import AdminResetPasswordPage from '../admin-auth/pages/AdminResetPasswordPage';
import AdminActionLinkHandler from '../admin-auth/pages/AdminActionLinkHandler';
import PatchNotesPage from '../pages/patch-notes/PatchNotesPage';
import ContactSubmissionsPage from '../contact-submissions/pages/ContactSubmissionsPage';
import SpecialAccessInvitesPage from '../special-access/pages/SpecialAccessInvitesPage';
import CustomPlansListPage from '../custom-plan/pages/CustomPlansListPage';
import CustomPlanFormPage from '../custom-plan/pages/CustomPlanFormPage';
import CustomPlanDetailPage from '../custom-plan/pages/CustomPlanDetailPage';
import AdminDocumentHead from '../components/AdminDocumentHead';
import SupportLoginPage from '../support-auth/pages/SupportLoginPage';
import { ADMIN_PATHS, adminPath } from '../constants/adminPaths';
import WorkspaceLoadingScreen from '../../components/loading/workspace/WorkspaceLoadingScreen';
import useImpersonationExitGate from '../../impersonation/exit/useImpersonationExitGate';

const ProtectedAdminRoute = ({ children }) => {
  const { admin, authUser, loading, error } = useAdminAuth();
  const returningFromImpersonation = useImpersonationExitGate(!loading);

  if (returningFromImpersonation) {
    return <WorkspaceLoadingScreen message="Preparing admin workspace..." />;
  }

  if (loading || (authUser && !admin && error)) {
    return <PageSkeleton fill label="Loading admin" />;
  }

  if (!admin) {
    return <Navigate to={adminPath(ADMIN_PATHS.LOGIN)} replace />;
  }

  return children;
};

const AdminRoutes = () => {
  const { hasPermission } = useAdminPermissions();

  return (
    <>
      <AdminDocumentHead />
      <AdminThemeProvider>
      <MaintenanceProvider canWrite>
        <WebsiteMaintenanceProvider canWrite>
          <ImpersonationProvider>
            <Routes>
              <Route path="paladin" element={<AdminSetup />} />
              <Route path="support-login" element={<SupportLoginPage />} />
              <Route path="login" element={<AdminLogin />} />
              <Route path="forgot-password" element={<AdminForgotPasswordPage />} />
              <Route path="reset-password" element={<AdminResetPasswordPage />} />
              <Route path="__/auth/action" element={<AdminActionLinkHandler />} />
              <Route path="magic-link" element={<AdminMagicLinkPage />} />
              <Route path="invite/accept" element={<AdminInviteAcceptPage />} />
              
              <Route
                element={
                  <ProtectedAdminRoute>
                    <ResponsiveAdminLayout />
                  </ProtectedAdminRoute>
                }
              >
                <Route index element={<Navigate to={hasPermission(ADMIN_PERMISSIONS.SUPPORT.VIEW) && !hasPermission(ADMIN_PERMISSIONS.DASHBOARD.VIEW) ? 'support' : 'dashboard'} replace />} />
                <Route path="dashboard" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.DASHBOARD.VIEW]}>
                    <ResponsiveDashboard />
                  </AdminPermissionRoute>
                } />
                <Route path="app-settings" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.ADMIN.MANAGE]}>
                    <AppSettings />
                  </AdminPermissionRoute>
                } />
                <Route path="demos" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.DEMOS.VIEW]}>
                    <Demos />
                  </AdminPermissionRoute>
                } />
                <Route path="contact-submissions" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.CONTACT_SUBMISSIONS.VIEW]}>
                    <ContactSubmissionsPage />
                  </AdminPermissionRoute>
                } />
                <Route
                  path="support"
                  element={
                    <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.SUPPORT.VIEW]}>
                      <Outlet />
                    </AdminPermissionRoute>
                  }
                >
                  <Route index element={<AdminTicketingListPage />} />
                  <Route path=":companyId/:ticketId" element={<AdminTicketingTicketPage />} />
                </Route>
                <Route path="settings" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.SETTINGS.VIEW]}>
                    <ResponsiveSettings />
                  </AdminPermissionRoute>
                } />
                <Route path="tools" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.ADMIN.MANAGE, ADMIN_PERMISSIONS.PAYMENTS.MANAGE]}>
                    <AdminTools />
                  </AdminPermissionRoute>
                } />
                <Route path="tools/purge-company" element={
                  <SuperAdminRoute>
                    <AdminPurgeCompanyPage />
                  </SuperAdminRoute>
                } />
                <Route path="special-access/invites" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.ADMIN.MANAGE, ADMIN_PERMISSIONS.PAYMENTS.MANAGE]}>
                    <SpecialAccessInvitesPage />
                  </AdminPermissionRoute>
                } />
                <Route path="custom-plans" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.ADMIN.MANAGE, ADMIN_PERMISSIONS.PAYMENTS.MANAGE]}>
                    <CustomPlansListPage />
                  </AdminPermissionRoute>
                } />
                <Route path="custom-plans/new" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.ADMIN.MANAGE, ADMIN_PERMISSIONS.PAYMENTS.MANAGE]}>
                    <CustomPlanFormPage />
                  </AdminPermissionRoute>
                } />
                <Route path="custom-plans/:planId" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.ADMIN.MANAGE, ADMIN_PERMISSIONS.PAYMENTS.MANAGE]}>
                    <CustomPlanDetailPage />
                  </AdminPermissionRoute>
                } />
                <Route path="users" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.USERS.VIEW]}>
                    <UserList />
                  </AdminPermissionRoute>
                } />
                <Route path="users/:userId" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.USERS.VIEW]}>
                    <UserProfile />
                  </AdminPermissionRoute>
                } />
                <Route path="companies/:companyId" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.USERS.VIEW]}>
                    <AdminCompanyProfile />
                  </AdminPermissionRoute>
                } />
                <Route path="signups" element={<AllSignups />} />
                <Route path="impersonation" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.IMPERSONATION.VIEW]}>
                    <ImpersonationPage />
                  </AdminPermissionRoute>
                } />
                <Route path="admin-management/create" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.ADMIN.MANAGE]}>
                    <CreateAdmin />
                  </AdminPermissionRoute>
                } />
                <Route path="admin-management/invite-create" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.ADMIN.MANAGE]}>
                    <InviteCreateAdmin />
                  </AdminPermissionRoute>
                } />
                <Route path="admin-management/invites" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.ADMIN.MANAGE]}>
                    <AdminInvitesPage />
                  </AdminPermissionRoute>
                } />
                <Route path="packages" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.PACKAGES.VIEW]}>
                    <PackageManagement />
                  </AdminPermissionRoute>
                } />
                <Route path="packages/new" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.PACKAGES.EDIT]}>
                    <PackageFormPage />
                  </AdminPermissionRoute>
                } />
                <Route path="packages/:packageId/edit" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.PACKAGES.EDIT]}>
                    <PackageFormPage />
                  </AdminPermissionRoute>
                } />
                <Route path="tools/package-grace-period-test" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.ADMIN.MANAGE]}>
                    <PackageGracePeriodTestPage />
                  </AdminPermissionRoute>
                } />
                <Route path="payments/authorize" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.PAYMENTS.MANAGE]}>
                    <AuthorizeManualCharge />
                  </AdminPermissionRoute>
                } />
                <Route path="payments/transactions" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.PAYMENTS.MANAGE]}>
                    <Transactions />
                  </AdminPermissionRoute>
                } />
                <Route path="sms-logs" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.SMS_LOGS.VIEW]}>
                    <SMSLogs />
                  </AdminPermissionRoute>
                } />
                <Route path="impersonation-logs" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.ADMIN.MANAGE]}>
                    <ImpersonationLogs />
                  </AdminPermissionRoute>
                } />
                <Route path="patch-notes" element={
                  <AdminPermissionRoute requiredPermissions={[ADMIN_PERMISSIONS.ADMIN.MANAGE]}>
                    <PatchNotesPage />
                  </AdminPermissionRoute>
                } />
                <Route path="*" element={<Navigate to="dashboard" replace />} />
              </Route>
            </Routes>
          </ImpersonationProvider>
        </WebsiteMaintenanceProvider>
      </MaintenanceProvider>
    </AdminThemeProvider>
    </>
  );
};

export default AdminRoutes; 